import {setEStopTriggered} from 'stores/actions/instrumentAction';
import {FastRobotErrorcode, Feedback, ViewMode} from '../../constants/dataEnum';
import {A1OffsetCalibrationSubstep, SetupWizardStep, WizardFlag} from '../../constants/SetupWizardEnum';
import {hideLoading, showLoading, showSnackbar} from '../actions/appStateAction';
import {showModalSetTrayID} from '../actions/modalSetTrayIDStateAction';
import {removeTemporaryState} from '../actions/temporaryStateAction';
import {setWizardAllowedNext, setWizardFlag, setCurrentStepData, clearWizardFlag} from '../actions/wizardStateAction';
import {resolveIntl, setErrorCodeNumber} from './commonBusiness';
import {
  showConfirmationModal,
  showDefaultErrorModal,
  showIsBusyModal,
  showModalDetectHardwareChange,
  showModalMisalignedTray,
} from './commonModalBusiness';
// eslint-disable-next-line import/no-cycle
import {showEmergencyStopModal} from './emergencyStopBusiness';
import {abortAutoCalibration, moveToIdlePosition} from './instrumentBusiness';
import {
  noLicenseActivatedModal,
  showAbnormalCalibrationDataModal,
  showCommonVisionErrorModal,
  showFailedToRunCalibrationModal,
  showModalMotorCollisionSkipped,
  showModalProblemWithTheHardware,
  showModalTouchProbeExtendedEnterAutomatically,
  showModalTouchProbeExtendFailed,
  showModalTouchProbeNotWorkingProperly,
  showModalTouchProbeRetractFailed,
  showModalTrayIdPositionNotFound,
  showMotorMoveErrorInvalidValue,
  showSafetyThresholdExceededModal,
  showStuckTipModal,
  showTouchProbeCollisionModal,
} from './modalStateBusinessAction';
import {getRunningProtocol} from './protocolManagementBusiness'; // eslint-disable-next-line import/no-cycle
import {fastRecovery} from './recoveryBusiness';
import {defaultTargetDeck, getCurrentPos, retractTouchProbe} from './trayCalibrationBusiness';
import {showMissingTrayModal, showTrayUncalibratedModal} from './trayModalBusiness';

import {setStepDataByName} from './setupWizardBusiness/commonSetupWizardBusiness';
import {navigateSetupWizardToStep} from './setupWizardBusiness/setupWizardNavigationBusiness';
import {showLoadCellErrorModal} from './loadCellErrorBusiness';
import {openTrayCalibrationPage} from './menuBusiness';
import {handleIncompatibleVisionVersionError} from './plateRecognitionBusiness';

const intl = resolveIntl();

export const isLoadCellError = (fastErrorCode) =>
  fastErrorCode === FastRobotErrorcode.LOAD_CELL_VALUE_NO_CHANGE ||
  fastErrorCode === FastRobotErrorcode.LOAD_CELL_DRIVER_FAULT ||
  fastErrorCode === FastRobotErrorcode.LOAD_CELL_READ_TIMEOUT;

export function fastCommonActuatorAPIErrorHandler(
  fastErrorCode,
  successCallback,
  alternateSuccessCallback,
  emergencyStopCallback,
) {
  return async (dispatch, getState) => {
    console.log('fastCommonActuatorAPIErrorHandler', fastErrorCode);
    const state = getState();
    const {appState, wizardState} = state;
    const {viewMode} = appState;

    await dispatch(setErrorCodeNumber(fastErrorCode));

    switch (fastErrorCode) {
      case FastRobotErrorcode.NotReady:
      case FastRobotErrorcode.ActuatorEncoderError:
      case FastRobotErrorcode.ActuatorFollowingErrorIdle:
      case FastRobotErrorcode.ActuatorFollowingErrorMoving:
      case FastRobotErrorcode.ActuatorNotReady:
      case FastRobotErrorcode.ActuatorUnexpectedTrigger:
      case FastRobotErrorcode.ActuatorAborted:
      case FastRobotErrorcode.LOAD_CELL_VALUE_NO_CHANGE:
      case FastRobotErrorcode.LOAD_CELL_DRIVER_FAULT:
      case FastRobotErrorcode.LOAD_CELL_READ_TIMEOUT: {
        const {stepName} = wizardState;

        let idTextModal;
        if (
          viewMode === ViewMode.TRAY_CALIBRATION ||
          (viewMode === ViewMode.SETUP_WIZARD && stepName === SetupWizardStep.TRAY_CALIBRATION)
        ) {
          idTextModal = 'trayCalibrationContainer.modal.headCollisionConfirmation';
        } else if (
          viewMode === ViewMode.PLATE_TYPE_EDITOR ||
          (viewMode === ViewMode.SETUP_WIZARD && stepName === SetupWizardStep.PLATE_DEFINITION)
        ) {
          idTextModal = 'plateTypeEditorContainer.modal.headCollisionConfirmation';
        } else if (viewMode === ViewMode.SETUP_WIZARD && stepName === SetupWizardStep.VISION_CALIBRATION) {
          idTextModal = 'modal.setupWizard.visionCalibration';
        }

        // button to update A1 offset in preparation wizard will be disabled if there's error
        if (viewMode === ViewMode.SETUP_WIZARD && stepName === SetupWizardStep.A1_OFFSET_CALIBRATION) {
          dispatch(setStepDataByName(SetupWizardStep.A1_OFFSET_CALIBRATION, {isUpdateA1OffsetAllowed: false}));
        }
        if (isLoadCellError(fastErrorCode)) {
          dispatch(setCurrentStepData(fastErrorCode));
          dispatch(showLoadCellErrorModal(alternateSuccessCallback, fastErrorCode, successCallback));
        } else {
          dispatch(
            showModalMotorCollisionSkipped(
              async () => {
                dispatch(showLoading(intl.formatMessage({id: 'recoveryAction.loading'})));
                await dispatch(
                  fastRecovery(() => {
                    if (idTextModal) {
                      dispatch(showConfirmationModal(idTextModal, successCallback, alternateSuccessCallback));
                    } else {
                      successCallback();
                    }
                  }),
                );
              },
              async () => {
                dispatch(showLoading(intl.formatMessage({id: 'recoveryAction.loading'})));
                await dispatch(fastRecovery());
                dispatch(hideLoading());
              },
            ),
          );
        }
        break;
      }
      case FastRobotErrorcode.ActuatorIllegalPos: {
        dispatch(showMotorMoveErrorInvalidValue());
        break;
      }
      case FastRobotErrorcode.Busy: {
        const runningProtocolResponse = await dispatch(getRunningProtocol());
        const {username} = runningProtocolResponse?.payload?.user || {};
        dispatch(showIsBusyModal(username));
        break;
      }
      case FastRobotErrorcode.TouchProbeExpanded: {
        dispatch(showModalTouchProbeExtendedEnterAutomatically(successCallback));
        break;
      }
      case FastRobotErrorcode.TouchProbeExtendFailed: {
        const {temporaryState} = getState();
        let counter = 1;
        if (temporaryState?.touchProbeExtendFailed?.counter) {
          counter += temporaryState.touchProbeExtendFailed.counter;
        }
        if (counter < 3) {
          dispatch(
            showModalTouchProbeExtendFailed(counter, successCallback, fastCommonActuatorAPIErrorHandler, () => {
              alternateSuccessCallback(true);
            }),
          );
        } else {
          dispatch(removeTemporaryState('touchProbeExtendFailed'));
          dispatch(showModalTouchProbeNotWorkingProperly(counter, fastCommonActuatorAPIErrorHandler));
        }
        break;
      }
      case FastRobotErrorcode.TouchProbeRetractFailed: {
        dispatch(showModalTouchProbeRetractFailed());
        break;
      }
      case FastRobotErrorcode.TOUCH_PROBE_COLLISION: {
        dispatch(showTouchProbeCollisionModal(successCallback, alternateSuccessCallback, fastRecovery));
        break;
      }
      case FastRobotErrorcode.MissingTray: {
        dispatch(showMissingTrayModal(true, successCallback, alternateSuccessCallback));
        break;
      }
      case FastRobotErrorcode.TrayUncalibrated: {
        const calibrateTrayCallback = () => dispatch(openTrayCalibrationPage());

        dispatch(showTrayUncalibratedModal(calibrateTrayCallback));
        break;
      }
      case FastRobotErrorcode.ActuatorEmergencyStop: {
        dispatch(setEStopTriggered(true));
        dispatch(showEmergencyStopModal(fastRecovery, false, emergencyStopCallback));
        break;
      }
      case FastRobotErrorcode.NO_LICENSE_ACTIVATED: {
        dispatch(noLicenseActivatedModal());
        break;
      }
      case FastRobotErrorcode.NO_TRAY_ID_FOUND: {
        const {modalSetTrayIDState} = state;
        dispatch(showModalSetTrayID(modalSetTrayIDState.type));

        if (viewMode === ViewMode.SETUP_WIZARD) {
          dispatch(clearWizardFlag());
          dispatch(hideLoading());
        }
        break;
      }
      case FastRobotErrorcode.ActuatorWaitMotionTimeout: {
        dispatch(
          showModalProblemWithTheHardware(fastErrorCode, async () => {
            await dispatch(fastRecovery());
            if (viewMode === ViewMode.SETUP_WIZARD) {
              dispatch(setWizardFlag(WizardFlag.ERROR));
              dispatch(setWizardAllowedNext(false));
            }
            if (successCallback) successCallback();
          }),
        );
        break;
      }
      case FastRobotErrorcode.A1_OFFSET_UNFIT: {
        dispatch(showSafetyThresholdExceededModal());
        break;
      }
      case FastRobotErrorcode.INCOMPATIBLE_VISION_VERSION: {
        dispatch(handleIncompatibleVisionVersionError());
        break;
      }
      case FastRobotErrorcode.STUCK_TIPS_DETECTED: {
        dispatch(showStuckTipModal(() => dispatch(fastRecovery(successCallback))));
        break;
      }
      case FastRobotErrorcode.VISION_MULTIPLE_TRAY_ID_FOUND:
      case FastRobotErrorcode.FAILED_TO_COMMUNICATE_WITH_RASPI0:
      case FastRobotErrorcode.BARCODE_SCAN_FAILED:
      case FastRobotErrorcode.BARCODE_NOT_UNIQUE:
      case FastRobotErrorcode.CORAL_NOT_CONNECTED:
      case FastRobotErrorcode.CAMERA_NOT_CONNECTED:
      case FastRobotErrorcode.SSH_CONNECTION_FAILED:
      case FastRobotErrorcode.MIRROR_FIDUCIAL_NOT_FOUND:
      case FastRobotErrorcode.VISION_CALIBRATION_FAILED:
      case FastRobotErrorcode.CORAL_AND_CAMERA_NOT_CONNECTED:
      case FastRobotErrorcode.WRONG_IMAGE_SHAPE:
      case FastRobotErrorcode.WRONG_DETECTION_BOX_DIMENSION:
      case FastRobotErrorcode.WRONG_IMAGE_DETECTION:
      case FastRobotErrorcode.PREDICT_IMAGE_TIMEOUT:
      case FastRobotErrorcode.PREDICT_IMAGE_INVOKE_FAILED:
      case FastRobotErrorcode.DESTINATION_HOST_UNREACHABLE:
      case FastRobotErrorcode.PI0_SERVICE_INACTIVE:
      case FastRobotErrorcode.PI0_SERVICE_FAST_VISION_INACTIVE:
      case FastRobotErrorcode.PI0_SERVICE_SPI2VCAN_INACTIVE:
      case FastRobotErrorcode.CAN_COMMUNICATION_ISSUE:
      case FastRobotErrorcode.FILE_MODEL_DOESNT_EXIST:
      case FastRobotErrorcode.FILE_MODEL_CORRUPTED:
      case FastRobotErrorcode.WRONG_MODEL_IS_USED:
      case FastRobotErrorcode.INVALID_CONFIG_SELECTOR:
      case FastRobotErrorcode.VISION_SERVICE_CRASHES:
        dispatch(showCommonVisionErrorModal({errorCode: fastErrorCode}, successCallback, alternateSuccessCallback));
        break;
      case FastRobotErrorcode.CADDY_NOT_FOUND:
        dispatch(showCommonVisionErrorModal({errorCode: fastErrorCode}, successCallback, alternateSuccessCallback));
        break;
      case FastRobotErrorcode.MISALIGNMENT_TRAY: {
        if (viewMode === ViewMode.SETUP_WIZARD && wizardState.stepName === SetupWizardStep.A1_OFFSET_CALIBRATION) {
          dispatch(
            showModalMisalignedTray(() => {
              dispatch(setWizardAllowedNext(false));
              dispatch(
                setCurrentStepData({
                  currentSubstep: A1OffsetCalibrationSubstep.GUIDE_IMAGE,
                  isUpdateA1OffsetAllowed: false,
                }),
              );
              dispatch(navigateSetupWizardToStep(SetupWizardStep.TRAY_CALIBRATION));
            }),
          );
        }
        break;
      }
      case FastRobotErrorcode.MISS_ALIGN_YAW:
      case FastRobotErrorcode.MISS_ALIGN_ROLL:
      case FastRobotErrorcode.MISS_ALIGN_PITCH: {
        dispatch(showModalDetectHardwareChange(fastRecovery));
        break;
      }
      case FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND: {
        dispatch(showModalTrayIdPositionNotFound(successCallback, alternateSuccessCallback));
        break;
      }
      default: {
        dispatch(showDefaultErrorModal(fastErrorCode));
        break;
      }
    }
  };
}

export const autoCalibrationErrorHandler =
  (response, autoCalibration, callback = () => {}) =>
  async (dispatch) => {
    if (response.errorCode !== FastRobotErrorcode.NoError) {
      const lastDeck = response?.tray?.lastDeck;
      const lastDeckIndex = lastDeck && defaultTargetDeck.indexOf(response.tray.lastDeck);
      const remainingTargetDeck = lastDeckIndex === -1 ? [lastDeckIndex] : defaultTargetDeck.slice(lastDeckIndex);

      const showTrayCalibrationAbortedToast = () => {
        dispatch(
          showSnackbar(
            Feedback.ERROR,
            intl.formatMessage({
              id: 'trayCalibrationContainer.notification.aborted',
            }),
          ),
        );
      };

      await dispatch(setErrorCodeNumber(response.errorCode));

      switch (response.errorCode) {
        case FastRobotErrorcode.MissingTray: {
          const successCallback = () => dispatch(autoCalibration(remainingTargetDeck, true));
          const failedCallback = () => {
            showTrayCalibrationAbortedToast();
            callback(response);
          };

          dispatch(showMissingTrayModal(true, successCallback, failedCallback));

          break;
        }
        case FastRobotErrorcode.MISALIGNMENT_TRAY: {
          // rollPitchYaw exceeds thresholdRPY
          dispatch(showFailedToRunCalibrationModal(response.tray.rollPitchYaw, response.tray.thresholdRPY));
          callback(response);
          break;
        }
        case FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND: {
          const secondButtonCallback = () => {
            dispatch(
              fastRecovery(() => {
                dispatch(getCurrentPos());
                callback(response);
              }),
            );
          };

          const successCallback = () => dispatch(autoCalibration([], false));

          dispatch(fastCommonActuatorAPIErrorHandler(response.errorCode, successCallback, secondButtonCallback));
          break;
        }
        case FastRobotErrorcode.CALIBRATION_EXCEED_THRESHOLD: {
          dispatch(
            showAbnormalCalibrationDataModal(
              () => dispatch(autoCalibration(remainingTargetDeck, true)),
              async () => {
                await dispatch(moveToIdlePosition());
                callback(response);
              },
            ),
          );
          break;
        }
        default: {
          const firstButtonCallback = async () => {
            await dispatch(autoCalibration(remainingTargetDeck, true));
          };
          const secondButtonCallback = async (isTouchProbeExtendAborted) => {
            // show loading
            dispatch(
              showLoading(
                intl.formatMessage({
                  id: 'instrumentControlHub.AbortProtocolStart.loading',
                }),
              ),
            );

            await dispatch(moveToIdlePosition());

            if (isTouchProbeExtendAborted) {
              await dispatch(abortAutoCalibration());
              showTrayCalibrationAbortedToast();
              callback({errorCode: FastRobotErrorcode.NoError, isAborted: true});
              return;
            }

            await dispatch(retractTouchProbe());

            // hide loading
            dispatch(hideLoading());

            // show notificaiton
            showTrayCalibrationAbortedToast();
            callback(response);
          };

          const emergencyStopCallback = () => callback(response);

          dispatch(
            fastCommonActuatorAPIErrorHandler(
              response.errorCode,
              firstButtonCallback,
              secondButtonCallback,
              emergencyStopCallback,
            ),
          );
          break;
        }
      }
    } else {
      callback(response);
    }
  };

export default autoCalibrationErrorHandler;
